import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, NumberInput, Select, Stack, Table, Text, Code } from '@mantine/core';
import { IconArrowBackUp, IconHandGrab } from '@tabler/icons-react';
import type { DirectedPickLineDto, PickLineDto } from './api';

interface LocationOption {
  value: string;
  label: string;
}

interface Props {
  line: PickLineDto;
  /** Where the route says this item lives, so the from-location defaults to something sensible. */
  suggested: DirectedPickLineDto | undefined;
  locations: LocationOption[];
  /** Once the first line is picked the dock is fixed, and the row stops asking. */
  stagingLocationId: string | null;
  disabled: boolean;
  busy: boolean;
  onPick: (input: {
    quantity: number;
    fromLocationId: string;
    lotNumber?: string;
    serialNumber?: string;
  }) => void;
  /**
   * The lot is only needed when the line was picked from more than one. The serial is needed
   * whenever the line took more than one UNIT, which for a serialized item is any line of two
   * or more — each scan is a different unit, so such a line never names one.
   */
  onReverse: (quantity: number, lotNumber?: string, serialNumber?: string) => void;
}

/**
 * One row of the desktop pick grid — legacy's `_GridViewOrderPickPartial`.
 *
 * The quantity and the from-location are edited IN the row rather than in a dialog, which is what
 * makes a desk fast: a picker working from a printed sheet types a number, presses Pick, and the
 * focus is already on the next line. A modal per line turns twenty lines into forty clicks.
 *
 * The quantity defaults to everything still owed, because that is the answer most of the time.
 */
export function PickLineRow({
  line,
  suggested,
  locations,
  stagingLocationId,
  disabled,
  busy,
  onPick,
  onReverse,
}: Props) {
  const { t } = useTranslation();

  const [quantity, setQuantity] = useState<number | string>(line.quantityOutstanding);
  const [from, setFrom] = useState<string | null>(
    suggested?.suggestedLocationId ?? line.fromLocationId ?? null,
  );
  const [lot, setLot] = useState('');
  const [serial, setSerial] = useState('');

  // Re-default when the line changes underneath — after a pick, what is left is the new answer.
  useEffect(() => {
    setQuantity(line.quantityOutstanding);
  }, [line.quantityOutstanding]);

  useEffect(() => {
    setFrom((current) => current ?? suggested?.suggestedLocationId ?? line.fromLocationId ?? null);
  }, [suggested?.suggestedLocationId, line.fromLocationId]);

  const amount = typeof quantity === 'number' ? quantity : Number(quantity);
  const canPick =
    !disabled && !busy && from !== null && stagingLocationId !== null && amount > 0
    && amount <= line.quantityOutstanding;
  const canReverse = !disabled && !busy && line.quantityPicked > 0;

  return (
    <Table.Tr>
      <Table.Td>{line.lineNumber}</Table.Td>
      <Table.Td>
        <Stack gap={0}>
          <Code>{line.itemNumber}</Code>
          <Text size="xs" c="dimmed">
            {line.itemDescription}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        {line.quantityRequested} {line.uomCode}
      </Table.Td>
      <Table.Td>{line.quantityPicked}</Table.Td>
      <Table.Td>
        <Text fw={line.quantityOutstanding > 0 ? 500 : 400} c={line.quantityOutstanding === 0 ? 'green' : undefined}>
          {line.quantityOutstanding}
        </Text>
      </Table.Td>
      <Table.Td>
        <NumberInput
          value={quantity}
          onChange={setQuantity}
          min={0}
          max={line.quantityOutstanding}
          step={1}
          w={90}
          size="sm"
          disabled={disabled || line.quantityOutstanding === 0}
          aria-label={t('picks.quantityFor', { line: line.lineNumber })}
        />
      </Table.Td>
      <Table.Td>
        <Select
          data={locations}
          value={from}
          onChange={setFrom}
          searchable
          w={170}
          size="sm"
          disabled={disabled || line.quantityOutstanding === 0}
          placeholder={t('picks.chooseFrom')}
          aria-label={t('picks.fromFor', { line: line.lineNumber })}
        />
      </Table.Td>
      {line.isLotControlled && (
        <Table.Td>
          <input
            value={lot}
            onChange={(e) => setLot(e.currentTarget.value)}
            placeholder={t('picks.lot')}
            aria-label={t('picks.lotFor', { line: line.lineNumber })}
            style={{ width: 110 }}
          />
        </Table.Td>
      )}
      {/* Uppercased, because usp_Serial_ResolveOrRegister stores it that way and a
          lower-case entry would otherwise read as a serial nobody has received. */}
      {line.isSerialized && (
        <Table.Td>
          <input
            value={serial}
            onChange={(e) => setSerial(e.currentTarget.value.toUpperCase())}
            placeholder={t('picks.serial')}
            aria-label={t('picks.serialFor', { line: line.lineNumber })}
            style={{ width: 110 }}
          />
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Button
            size="xs"
            color="invetrak"
            leftSection={<IconHandGrab size={14} />}
            disabled={!canPick}
            onClick={() =>
              onPick({
                // One serial is one unit, whatever the quantity box says. The server
                // refuses anything else (086, error 54273) and the desk should not
                // make somebody discover that by being told off.
                quantity: line.isSerialized ? 1 : amount,
                fromLocationId: from!,
                lotNumber: lot.trim() || undefined,
                serialNumber: serial.trim() || undefined,
              })
            }
          >
            {t('picks.pick')}
          </Button>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconArrowBackUp size={14} />}
            disabled={!canReverse}
            onClick={() =>
              onReverse(
                line.isSerialized
                  ? 1
                  : amount > 0 ? Math.min(amount, line.quantityPicked) : line.quantityPicked,
                // The same boxes the pick uses. Empty is fine for a single-lot line, and
                // for a serialized line of exactly one unit.
                lot.trim() || undefined,
                serial.trim() || undefined,
              )
            }
          >
            {t('picks.reverse')}
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
